/**
 * La déclaration initiale de cotisation foncière des entreprises, 1447-C-SD.
 *
 * PREMIER VRAI CERFA DE LA RUBRIQUE. Les trois documents précédents sont des
 * lettres que nous composons ; celui-ci est un imprimé officiel à champs, que
 * nous REMPLISSONS. Le formulaire vit dans public/formulaires, et ce module ne
 * fait que dire quelle réponse va dans quelle case. Le remplissage lui-même est
 * dans lib/tools/officialFormPdf.js.
 *
 * LE MILLÉSIME EST LE POINT FRAGILE, et il est surveillé. L'administration
 * publie un imprimé par an : celui de 2025 sert aux établissements créés en
 * 2025, pour la CFE 2026. Un créateur de 2026 attend l'imprimé 2026, publié
 * chaque année au début de l'automne (18/10/2024 pour le millésime 2024,
 * 23/09/2025 pour le millésime 2025, d'après les métadonnées des fichiers).
 * `pages/api/webhooks/tools-review.js` interroge impots.gouv chaque semaine et
 * prévient dès que le millésime suivant paraît : il n'y a plus qu'à déposer le
 * fichier et à changer les quatre lignes ci-dessous.
 *
 * LES NOMS DE CHAMPS SONT STABLES d'un millésime à l'autre : vérifié entre 2024
 * et 2025, cent champs identiques, aucun ajout ni retrait. La cartographie
 * ci-dessous devrait donc survivre au changement d'imprimé, ce que le harnais
 * revérifiera le jour venu.
 *
 * CE QUE NOUS NE REMPLISSONS PAS, ET POURQUOI. Le cadre C de la page 3 décrit
 * les locaux, et la notice en dispense expressément qui coche le cadre A2,
 * c'est-à-dire qui n'a pas de local dédié à son activité. C'est le cas du
 * micro-entrepreneur qui travaille chez lui ou chez ses clients, à qui cet
 * outil s'adresse. Quiconque a un local dédié est renvoyé vers le formulaire
 * complet plutôt que servi à moitié.
 */

import { dateEnToutesLettres, identite, nettoyer } from "./documentText.js";

/**
 * L'imprimé embarqué, et ce qu'il couvre.
 *
 * `creationsDe` est l'année des créations que cet imprimé déclare, `cfeDe`
 * l'année d'imposition qui en découle. Les deux servent au tri du parcours et
 * au contrôle de fraîcheur.
 */
export const MILLESIME = {
    annee: 2025,
    creationsDe: 2025,
    cfeDe: 2026,
    cerfa: "14187*16",
    fichier: "/formulaires/1447-c-sd-2025.pdf",
    /** Date limite de dépôt pour les créations de l'année couverte. */
    deposerAvantLe: "2025-12-31",
};

/**
 * Le département d'après le code postal.
 *
 * Deviné, pas déduit : le formulaire demande le département du lieu
 * d'imposition, et un code postal ne le donne qu'en général. La Corse en est la
 * preuve, où 20 ne dit pas si c'est 2A ou 2B. Le parcours pré-remplit donc le
 * champ avec cette valeur et laisse le lecteur la corriger.
 */
export function departementProbable(codePostal) {
    const cp = nettoyer(codePostal);
    if (!/^\d{5}$/.test(cp)) return "";
    if (cp.startsWith("20")) return "";
    // Outre-mer : les trois premiers chiffres font le département.
    if (cp.startsWith("97") || cp.startsWith("98")) return cp.slice(0, 3);
    return cp.slice(0, 2);
}

/**
 * Qui dépose ce formulaire, et qui n'a rien à y faire.
 *
 * Trois sorties : l'année de création décide, et le local dédié renvoie au
 * formulaire complet.
 */
export function concerne(reponses) {
    const r = reponses || {};
    // Les deux bords du millésime ne sont pas la même personne, et on leur
    // répondait la même chose. Qui a créé AVANT a sa déclaration initiale
    // derrière lui, il n'y a rien à attendre. Qui a créé l'ANNÉE EN COURS doit
    // déposer avant le 31 décembre, mais l'imprimé de son année n'est pas
    // encore publié : celui-là, on le prévient à sa parution. C'est le cas le
    // plus courant neuf mois sur douze, et il valait mieux qu'un refus.
    if (r.anneeCreation === "anneeEnCours") return { concerne: false, motif: "anneeEnCours" };
    if (r.anneeCreation === "avant") return { concerne: false, motif: "avant" };
    // L'ancien code, gardé pour ne pas casser un état enregistré ailleurs.
    if (r.anneeCreation === "autreAnnee") return { concerne: false, motif: "avant" };
    if (r.anneeCreation === "pasEncore") return { concerne: false, motif: "pasEncore" };
    if (r.local === "dedie") return { concerne: false, motif: "localDedie" };
    if (!r.anneeCreation) return { concerne: null, motif: null };
    return { concerne: true, motif: null };
}

/**
 * Les cases de la première question, calculées et jamais écrites en dur.
 *
 * L'imprimé porte l'année qu'il couvre. Tant que l'administration n'a pas
 * publié celui de l'année en cours, il en manque une : le créateur de l'année
 * n'a pas de case où se reconnaître, alors qu'il a jusqu'au 31 décembre pour
 * déposer. On la lui ajoute, et elle disparaît d'elle-même le jour où MILLESIME
 * avance.
 */
export function choixDAnnee(aujourdhui) {
    const enCours = new Date(aujourdhui || Date.now()).getFullYear();
    const couverte = MILLESIME.creationsDe;
    const cases = [];
    if (enCours > couverte) {
        cases.push({ cle: "anneeEnCours", annee: enCours, attente: true });
    }
    cases.push({ cle: "cetteAnnee", annee: couverte, attente: false });
    cases.push({ cle: "avant", annee: couverte, attente: false });
    cases.push({ cle: "pasEncore", annee: null, attente: false });
    return cases;
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses, aujourdhui) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    if (!concerne(r).concerne) {
        return { valide: false, manques: ["anneeCreation"], invalides, alertes };
    }

    const requis = [
        ["prenoms", r.prenoms],
        ["nom", r.nom],
        ["activites", r.activites],
        ["voie", r.voie],
        ["commune", r.commune],
        ["departement", r.departement],
        ["lieuDeSignature", r.lieuDeSignature],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    const cp = nettoyer(r.codePostal);
    if (!cp) manques.push("codePostal");
    else if (!/^\d{5}$/.test(cp)) invalides.push({ champ: "codePostal", motif: "format" });

    // Le SIRET fait quatorze chiffres, espaces tolérés à la saisie.
    const siret = nettoyer(r.siret).replace(/\s/g, "");
    if (!siret) manques.push("siret");
    else if (!/^\d{14}$/.test(siret)) invalides.push({ champ: "siret", motif: "siret" });

    // Le code APE fait quatre chiffres et une lettre.
    const ape = nettoyer(r.codeApe).replace(/[\s.]/g, "").toUpperCase();
    if (ape && !/^\d{4}[A-Z]$/.test(ape)) invalides.push({ champ: "codeApe", motif: "ape" });

    for (const cle of ["dateCreation", "dateDeSignature"]) {
        const brut = nettoyer(r[cle]);
        if (!brut) manques.push(cle);
        else if (!dateEnToutesLettres(brut)) invalides.push({ champ: cle, motif: "format" });
    }

    // La date de création doit tomber dans l'année que l'imprimé couvre : c'est
    // toute la raison d'être du millésime.
    if (dateEnToutesLettres(r.dateCreation)) {
        const annee = Number(r.dateCreation.slice(0, 4));
        if (annee !== MILLESIME.creationsDe) {
            invalides.push({ champ: "dateCreation", motif: "horsMillesime" });
        }
    }

    if (r.microSocial && !dateEnToutesLettres(r.dateMicroSocial)) {
        if (nettoyer(r.dateMicroSocial)) {
            invalides.push({ champ: "dateMicroSocial", motif: "format" });
        } else manques.push("dateMicroSocial");
    }

    // Le cadre A2 demande la surface dès qu'on le coche : un bureau dans un
    // salon vaut un mètre carré, mais il en vaut un.
    const surface = nettoyer(r.surface);
    if (!surface) manques.push("surface");
    else if (!/^\d{1,4}$/.test(surface) || Number(surface) < 1) {
        invalides.push({ champ: "surface", motif: "format" });
    }

    // Le repère 7 du cadre A1 attend une réponse, OUI ou NON, et un formulaire
    // fiscal ne répond pas à la place du déclarant : pas de valeur par défaut.
    if (r.artisan !== "oui" && r.artisan !== "non") manques.push("artisan");

    const ca = nettoyer(r.chiffreDAffaires).replace(/\s/g, "");
    if (!ca) manques.push("chiffreDAffaires");
    else if (!/^\d{1,9}$/.test(ca)) invalides.push({ champ: "chiffreDAffaires", motif: "format" });

    // Le dépôt hors délai ne se refuse pas : il se signale, parce que déposer
    // en retard vaut mieux que ne pas déposer.
    if (aujourdhui && new Date(aujourdhui) > new Date(`${MILLESIME.deposerAvantLe}T23:59:59Z`)) {
        alertes.push("horsDelai");
    }

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/**
 * La projection des réponses sur les cases du Cerfa.
 *
 * Les noms viennent du formulaire lui-même (`a1`, `b14`, `cac1a`...) et ne
 * veulent rien dire : le commentaire de chaque ligne porte le libellé imprimé,
 * pour qu'une relecture soit possible sans ouvrir le PDF.
 */
export function champsDuFormulaire(reponses) {
    const r = reponses || {};
    if (!valider(r, null).valide) return null;

    const nomComplet = identite({ prenoms: r.prenoms, nom: r.nom });
    const siret = nettoyer(r.siret).replace(/\s/g, "");
    const ape = nettoyer(r.codeApe).replace(/[\s.]/g, "").toUpperCase();
    const adresse = [nettoyer(r.voie), `${nettoyer(r.codePostal)} ${nettoyer(r.commune)}`]
        .filter(Boolean)
        .join(", ");

    const textes = {
        a1: nettoyer(r.departement), // DÉPARTEMENT
        a2: nettoyer(r.commune), // COMMUNE DU LIEU D'IMPOSITION
        a5: nomComplet, // A1 Dénomination ou nom et prénom
        a6: nettoyer(r.activites), // A1 Activités exercées
        a7: adresse, // A1 Adresse dans la commune
        a8: adresse, // A1 Adresse d'envoi de l'avis d'imposition
        a9: siret, // A1 Numéro SIRET de l'établissement
        a10: ape, // A1 Code de l'activité (NACE)
        a15: nettoyer(r.surface), // A2 surface occupée à domicile, en m²
        a19: nettoyer(r.lieuDeSignature), // À ...
        a20: jourMoisAnnee(r.dateDeSignature), // le ...
        // a21, la case « Signature », reste vide : un nom dactylographié n'est
        // pas une signature, et le déclarant signe à la main ou dépose depuis
        // son espace professionnel authentifié.
        date1: jourMoisAnnee(r.dateCreation), // B1 repère 1, date de création
        b6: "0", // B1 repère 2, salariés de l'entreprise
        b14: nettoyer(r.chiffreDAffaires).replace(/\s/g, ""), // B1 repère 6, CA estimé
        b29: "0", // B2, salariés de l'établissement
    };

    // B2 : la date d'entrée dans le statut de micro-entrepreneur.
    if (r.microSocial) textes.b33 = jourMoisAnnee(r.dateMicroSocial);

    /* Une seule case d'origine, « début d'activité » : c'est celle d'une
       entreprise qui commence, là où « création d'établissement » désigne
       plutôt l'établissement qu'une entreprise existante ouvre en plus. La
       notice ne tranche pas et les guides divergent ; le récapitulatif montre
       la case retenue, et l'imprimé n'étant pas aplati, l'autre reste à portée
       de clic dans un lecteur de PDF. */
    const cases = [
        "cac1a", // A2, aucun autre local que le domicile
        "cac6_1", // B2, établissement où l'activité est exercée à titre principal
    ];
    if (r.microSocial) cases.push("cac7"); // B2, régime micro-social
    if (r.tempsPartiel) cases.push("ca7a"); // B2, activité à temps partiel

    /* Le repère 7 est une case double, OUI ou NON, dont les deux widgets
       portent des valeurs distinctes : /2 pour OUI, /1 pour NON, d'après leur
       position sur la page (le OUI est à gauche). Elle se pose autrement qu'une
       case simple, voir remplirFormulaire. */
    const choix = {
        cac1: r.artisan === "oui" ? "2" : "1",
        // A3 : « début d'activité » est le premier widget de cac3 (/1), le
        // second (/2) est « transfert d'activité ». Posé par valeur pour ne
        // jamais dépendre de l'ordre des widgets.
        cac3: "1",
    };

    return { textes, cases, choix, millesime: MILLESIME };
}

/** Une date ISO en JJ/MM/AAAA, la forme que le Cerfa attend dans ses cases. */
export function jourMoisAnnee(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
}

/**
 * Le récapitulatif montré à l'écran.
 *
 * Un PDF rempli ne se relit pas dans la page : le lecteur doit pouvoir vérifier
 * ce qui partira avant de télécharger, et sous une forme lisible plutôt que par
 * des noms de cases.
 */
export function recapitulatif(reponses) {
    const r = reponses || {};
    const champs = champsDuFormulaire(r);
    if (!champs) return null;

    return [
        ["Déclarant", identite({ prenoms: r.prenoms, nom: r.nom })],
        ["SIRET", nettoyer(r.siret).replace(/\s/g, "")],
        ["Activités exercées", nettoyer(r.activites)],
        ["Code APE", nettoyer(r.codeApe).toUpperCase() || "non renseigné"],
        ["Adresse de l'établissement", champs.textes.a7],
        ["Département d'imposition", nettoyer(r.departement)],
        ["Date de création", dateEnToutesLettres(r.dateCreation)],
        ["Chiffre d'affaires estimé", `${nettoyer(r.chiffreDAffaires).replace(/\s/g, "")} €`],
        ["Surface occupée à domicile", `${nettoyer(r.surface)} m²`],
        ["Régime micro-social", r.microSocial ? `oui, depuis le ${dateEnToutesLettres(r.dateMicroSocial)}` : "non déclaré"],
        ["Inscrit au RNE comme artisan", r.artisan === "oui" ? "oui" : "non"],
        ["Origine de l'établissement", "début d'activité"],
        ["Aucun autre local que le domicile", "case cochée"],
        ["Case signature", "laissée vide, à signer à la main"],
        ["Signature", `${nettoyer(r.lieuDeSignature)}, le ${dateEnToutesLettres(r.dateDeSignature)}`],
    ];
}
